import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SignOut, Envelope, CalendarCheck, TrayArrowDown, Trash, CheckCircle,
  ArrowClockwise, Buildings, Phone, ChatCircleText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const statusColors = {
  new: "bg-primary/15 text-primary",
  contacted: "bg-blue-500/15 text-blue-400",
  closed: "bg-white/10 text-muted-foreground",
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([api.get("/enquiries/stats"), api.get("/enquiries")]);
      setStats(s.data);
      setEnquiries(e.data);
    } catch {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (!ready || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Loading…
      </div>
    );
  }

  const typeFilters = ["demo", "contact", "calculator"];
  const filtered = enquiries.filter((e) => (filter === "all" ? true : typeFilters.includes(filter) ? e.enquiry_type === filter : e.status === filter));

  const cards = [
    { label: "Total enquiries", value: stats?.total ?? 0, icon: TrayArrowDown },
    { label: "Demo requests", value: stats?.demos ?? 0, icon: CalendarCheck },
    { label: "Contact messages", value: stats?.contacts ?? 0, icon: Envelope },
    { label: "New / unactioned", value: stats?.new ?? 0, icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-muted-foreground sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-white" data-testid="admin-refresh">
              <ArrowClockwise size={16} weight="bold" /> Refresh
            </button>
            <button onClick={doLogout} className="flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/15" data-testid="admin-logout">
              <SignOut size={16} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <h1 className="font-heading text-2xl font-bold text-white">Enquiries</h1>
        <p className="mt-1 text-sm text-muted-foreground">Demo bookings and contact messages captured from your website.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="rounded-2xl border border-white/10 bg-card p-5" data-testid={`stat-${c.label}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <Icon size={20} weight="duotone" className="text-primary" />
                </div>
                <p className="mt-2 font-heading text-3xl font-extrabold text-white">{c.value}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {["all", "demo", "contact", "calculator", "new", "contacted", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "border border-white/10 text-muted-foreground hover:text-white"}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <p className="py-16 text-center text-muted-foreground">Loading enquiries…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-white/10 py-20 text-center">
              <Envelope size={40} weight="duotone" className="mx-auto text-muted-foreground" />
              <p className="mt-3 text-muted-foreground">No enquiries here yet.</p>
            </div>
          ) : (
            filtered.map((e) => (
              <div key={e.id} className="rounded-2xl border border-white/10 bg-card p-6" data-testid="enquiry-row">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-lg font-semibold text-white">{e.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${e.enquiry_type === "demo" ? "bg-primary/15 text-primary" : e.enquiry_type === "calculator" ? "bg-amber-500/15 text-amber-400" : "bg-blue-500/15 text-blue-400"}`}>
                        {e.enquiry_type === "demo" ? "Demo" : e.enquiry_type === "calculator" ? "Calculator" : "Contact"}
                      </span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${statusColors[e.status] || statusColors.new}`}>{e.status}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm text-muted-foreground sm:grid-cols-2">
                      <span className="flex items-center gap-2"><Envelope size={15} /> <a href={`mailto:${e.email}`} className="hover:text-primary">{e.email}</a></span>
                      {e.telephone && <span className="flex items-center gap-2"><Phone size={15} /> {e.telephone}</span>}
                      {e.company && <span className="flex items-center gap-2"><Buildings size={15} /> {e.company}</span>}
                      {e.business_size && <span className="flex items-center gap-2"><CalendarCheck size={15} /> {e.business_size} engineers</span>}
                    </div>
                    {e.message && (
                      <p className="mt-3 flex items-start gap-2 rounded-xl bg-background p-3 text-sm text-muted-foreground">
                        <ChatCircleText size={16} className="mt-0.5 flex-none text-primary" /> {e.message}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground/70">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                  </div>

                  <div className="flex flex-none items-center gap-2">
                    <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                      <SelectTrigger className="w-[140px] bg-background" data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => remove(e.id)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive" data-testid="enquiry-delete" aria-label="Delete">
                      <Trash size={17} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
